import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Loader2, Smartphone, X } from 'lucide-react';

interface WhatsAppQRModalProps {
  open: boolean;
  onClose: () => void;
  qrCode: string;
  timeLeft: number;
  maxTime: number;
  isLoading: boolean;
  isSyncing: boolean;
}

export function WhatsAppQRModal({
  open,
  onClose,
  qrCode,
  timeLeft,
  maxTime,
  isLoading,
  isSyncing,
}: WhatsAppQRModalProps) {
  const formatTime = (ms: number) => {
    const seconds = Math.ceil(ms / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-[#25D366]" />
            Conectar WhatsApp
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {isLoading && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-12 w-12 text-[#25D366] animate-spin" />
              <p className="text-muted-foreground">Preparando conexão...</p>
            </div>
          )}

          {isSyncing && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="w-16 h-16 rounded-full bg-[#25D366]/10 flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-[#25D366] animate-spin" />
              </div>
              <div className="text-center">
                <p className="font-medium text-foreground">QR escaneado!</p>
                <p className="text-muted-foreground text-sm">Sincronizando mensagens...</p>
              </div>
            </div>
          )}

          {!isLoading && !isSyncing && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Tutorial lado esquerdo */}
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#25D366] text-white text-xs flex items-center justify-center font-medium">
                      1
                    </div>
                    <div>
                      <p className="text-sm font-medium">Abra o WhatsApp</p>
                      <p className="text-xs text-muted-foreground">No seu celular</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#25D366] text-white text-xs flex items-center justify-center font-medium">
                      2
                    </div>
                    <div>
                      <p className="text-sm font-medium">Toque em ⋮ ou ⚙️</p>
                      <p className="text-xs text-muted-foreground">Menu ou Configurações</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#25D366] text-white text-xs flex items-center justify-center font-medium">
                      3
                    </div>
                    <div>
                      <p className="text-sm font-medium">Dispositivos conectados</p>
                      <p className="text-xs text-muted-foreground">Depois toque em "Conectar um aparelho"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#25D366] text-white text-xs flex items-center justify-center font-medium">
                      4
                    </div>
                    <div>
                      <p className="text-sm font-medium">Escaneie o QR Code</p>
                      <p className="text-xs text-muted-foreground">Aponte a câmera para o código</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* QR Code lado direito */}
              <div className="flex flex-col items-center justify-center">
                {qrCode ? (
                  <>
                    <div className="p-3 bg-white rounded-xl shadow-inner border">
                      <img
                        src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                        alt="QR Code"
                        className="w-48 h-48"
                      />
                    </div>
                    <div className="mt-4 w-full space-y-2">
                      <Progress value={(timeLeft / maxTime) * 100} className="h-1.5" />
                      <p className="text-xs text-center text-muted-foreground">
                        Expira em {formatTime(timeLeft)}
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="w-48 h-48 bg-muted rounded-xl flex items-center justify-center">
                    <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
